import { Col, Row, Card, Form, Button, Image, Spinner, Alert } from 'react-bootstrap';
import { useAppContext } from 'providers/AppProvider';
import { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from 'store';
import axios from 'axios';
// if you have an action to update user in Redux, import it:
// import { setUser } from 'store/slices/authSlice';

const AVATAR_MAX_MB = 5;

type UserWithAvatar = {
  id?: number;
  uuid?: string;
  email?: string;
  name?: string;
  avatar_url?: string | null;
  [key: string]: any;
};

const UserAccountPage = () => {
  const dispatch = useDispatch();
  const trafficInformation = useSelector((state: RootState) => state.traffic.customers);
  const currentUser = useSelector((state: RootState) => state.auth?.user) as UserWithAvatar | null;
  const token = useSelector((state: RootState) => state.auth?.token) as string | null;

  const currentAvatarUrl: string | undefined = currentUser?.avatar_url ?? undefined;

  const {
    config: { isDark }
  } = useAppContext();

  // -------- Profile (name) form state --------
  const [profileName, setProfileName] = useState<string>(currentUser?.name ?? '');
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState<string | null>(null);

  useEffect(() => {
    setProfileName(currentUser?.name ?? '');
  }, [currentUser?.name]);

  const submitProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileError(null);
    setProfileSuccess(null);

    const name = profileName.trim();
    if (!name) {
      setProfileError('Name cannot be empty.');
      return;
    }

    try {
      setProfileLoading(true);
      const { data } = await axios.post(
        'https://api20.easychatdesk.com/update-name',
        { name },
        { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
      );

      // Expecting API to return the updated user
      const updatedUser: UserWithAvatar | undefined = data?.user;
      if (updatedUser) {
        // Update localStorage
        localStorage.setItem('authUser', JSON.stringify(updatedUser));
        if (updatedUser.avatar_url != null) {
          localStorage.setItem('avatar_url', updatedUser.avatar_url || '');
        }
        // If you have a Redux action to update the user, dispatch it here:
        // dispatch(setUser(updatedUser));
      }

      setProfileSuccess('Profile updated successfully.');
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to update profile.';
      setProfileError(msg);
    } finally {
      setProfileLoading(false);
    }
  };

  // -------- Password form state --------
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwSuccess, setPwSuccess] = useState<string | null>(null);

  const pwValid = useMemo(() => {
    if (newPassword.length < 8) return false;
    if (newPassword !== confirmNewPassword) return false;
    return true;
  }, [newPassword, confirmNewPassword]);

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwError(null);
    setPwSuccess(null);

    if (!pwValid) {
      setPwError('Please make sure the passwords match and have at least 8 characters.');
      return;
    }

    try {
      setPwLoading(true);
      await axios.post(
        'https://api20.easychatdesk.com/api/account/update-password',
        {
          new_password: newPassword,
          new_password_confirmation: confirmNewPassword
        },
        { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
      );
      setPwSuccess('Password updated successfully.');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to update password.';
      setPwError(msg);
    } finally {
      setPwLoading(false);
    }
  };

  // -------- Avatar form state --------
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [avatarSuccess, setAvatarSuccess] = useState<string | null>(null);

  const onPickAvatar: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setAvatarError(null);
    const file = e.target.files?.[0];
    if (!file) return;

    const sizeMb = file.size / (1024 * 1024);
    if (sizeMb > AVATAR_MAX_MB) {
      setAvatarError(`Please choose an image under ${AVATAR_MAX_MB} MB.`);
      return;
    }
    if (!/^image\/(png|jpeg|jpg|webp|gif)$/i.test(file.type)) {
      setAvatarError('Please choose a PNG, JPG, WEBP or GIF image.');
      return;
    }

    setAvatarFile(file);
    const reader = new FileReader();
    reader.onload = () => setAvatarPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const submitAvatar = async (e: React.FormEvent) => {
    e.preventDefault();
    setAvatarError(null);
    setAvatarSuccess(null);

    if (!avatarFile) {
      setAvatarError('Please choose an image file.');
      return;
    }

    try {
      setAvatarLoading(true);
      const form = new FormData();
      form.append('avatar', avatarFile, avatarFile.name);

      const { data } = await axios.post('https://api20.easychatdesk.com/upload-avatar', form, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
           'Content-Type': undefined as any,
        },
         transformRequest: (data) => data, 
      });

      setAvatarSuccess('Avatar updated successfully.');

      // If API returns updated user or avatar_url, keep state/localStorage in sync
      const updatedUser: UserWithAvatar | undefined = data?.user;
      const newAvatarUrl: string | undefined = data?.avatar_url ?? updatedUser?.avatar_url;

      if (newAvatarUrl) {
        localStorage.setItem('avatar_url', newAvatarUrl);
        if (updatedUser) {
          localStorage.setItem('authUser', JSON.stringify(updatedUser));
          // dispatch(setUser(updatedUser));
        } else if (currentUser) {
          const merged = { ...currentUser, avatar_url: newAvatarUrl };
          localStorage.setItem('authUser', JSON.stringify(merged));
          // dispatch(setUser(merged));
        }
      }

      setAvatarFile(null);
      setAvatarPreview(null);
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to update avatar.';
      setAvatarError(msg);
    } finally {
      setAvatarLoading(false);
    }
  };

  const effectiveAvatar = avatarPreview || currentAvatarUrl || undefined;

  return (
    <>
      <Row className="gy-3 mb-6 justify-content-between">
        <Col md={12} xs="auto">
          <h2 className="mb-2 text-body-emphasis">User Account</h2>
          <h5 className="text-body-tertiary fw-semibold">Control your personal details here.</h5>
        </Col>
      </Row>

      <Row className="g-4">
        {/* Avatar */}
        <Col lg={4}>
          <Card className="h-100">
            <Card.Header>
              <Card.Title as="h5" className="mb-0">Avatar</Card.Title>
            </Card.Header>
            <Card.Body>
              <div className="d-flex align-items-center gap-3 mb-3">
                <Image
                  src={effectiveAvatar || 'https://placehold.co/120x120?text=Avatar'}
                  roundedCircle
                  width={96}
                  height={96}
                  alt="Current avatar"
                  style={{ objectFit: 'cover' }}
                />
                <div className="text-body-secondary small">
                  JPG, PNG, WEBP or GIF. Max {AVATAR_MAX_MB} MB.
                </div>
              </div>

              {avatarError && <Alert variant="danger" className="py-2">{avatarError}</Alert>}
              {avatarSuccess && <Alert variant="success" className="py-2">{avatarSuccess}</Alert>}

              <Form onSubmit={submitAvatar}>
                <Form.Group controlId="avatarFile" className="mb-3">
                  <Form.Label>Select new image</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={onPickAvatar}
                    disabled={avatarLoading}
                  />
                </Form.Group>

                <div className="d-flex gap-2">
                  <Button type="submit" disabled={avatarLoading}>
                    {avatarLoading ? <Spinner size="sm" animation="border" /> : 'Update Avatar'}
                  </Button>
                  {avatarPreview && (
                    <Button
                      variant="outline-secondary"
                      type="button"
                      onClick={() => { setAvatarFile(null); setAvatarPreview(null); }}
                      disabled={avatarLoading}
                    >
                      Cancel Preview
                    </Button>
                  )}
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        {/* Profile (Name) */}
        <Col lg={4}>
          <Card className="h-100">
            <Card.Header>
              <Card.Title as="h5" className="mb-0">Profile</Card.Title>
            </Card.Header>
            <Card.Body>
              {profileError && <Alert variant="danger" className="py-2">{profileError}</Alert>}
              {profileSuccess && <Alert variant="success" className="py-2">{profileSuccess}</Alert>}

              <Form onSubmit={submitProfile}>
                <Form.Group controlId="profileEmail" className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={currentUser?.email ?? ''}
                    disabled
                    readOnly
                  />
                </Form.Group>

                <Form.Group controlId="profileName" className="mb-4">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                    placeholder="Your full name"
                    disabled={profileLoading}
                  />
                </Form.Group>

                <Button type="submit" disabled={profileLoading || !profileName.trim()}>
                  {profileLoading ? <Spinner size="sm" animation="border" /> : 'Update Profile'}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        {/* Password */}
        <Col lg={4}>
          <Card className="h-100">
            <Card.Header>
              <Card.Title as="h5" className="mb-0">Change Password</Card.Title>
            </Card.Header>
            <Card.Body>
              {pwError && <Alert variant="danger" className="py-2">{pwError}</Alert>}
              {pwSuccess && <Alert variant="success" className="py-2">{pwSuccess}</Alert>}

              <Form onSubmit={submitPassword}>
                <Form.Group controlId="newPassword" className="mb-3">
                  <Form.Label>New password</Form.Label>
                  <Form.Control
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter a strong password"
                    minLength={8}
                    autoComplete="new-password"
                    isInvalid={newPassword.length > 0 && newPassword.length < 8}
                    disabled={pwLoading}
                  />
                  <Form.Control.Feedback type="invalid">
                    Must be at least 8 characters.
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group controlId="confirmNewPassword" className="mb-4">
                  <Form.Label>Confirm new password</Form.Label>
                  <Form.Control
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    placeholder="Re-enter the new password"
                    autoComplete="new-password"
                    isInvalid={confirmNewPassword.length > 0 && confirmNewPassword !== newPassword}
                    disabled={pwLoading}
                  />
                  <Form.Control.Feedback type="invalid">
                    Passwords do not match.
                  </Form.Control.Feedback>
                </Form.Group>

                <Button type="submit" disabled={!pwValid || pwLoading}>
                  {pwLoading ? <Spinner size="sm" animation="border" /> : 'Update Password'}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default UserAccountPage;
